import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCbtExams, getCbtExamsLive, postCbtAttemptsVoid } from '@/api/teacher';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Banner, Input, Modal, PageHeader, QueryState, Stat, Table } from '@/components/kit/ui';
import { asList, errMsg, fmtDateTime, unwrap } from '@/components/kit/data';
import { ExamSelect, type ExamRow } from '../shared';

interface LiveAttempt { attempt_id: number; student_id: number; name: string; admission_no: string; status: string; answered: number; time_left_seconds: number; last_saved_at: string | null; flagged: boolean; events: Record<string, number> }
interface Live { status: string; server_time: string; eligible: number; questions: number; counts: { not_started: number; in_progress: number; finished: number }; attempts: LiveAttempt[] }

const mins = (s: number) => (s > 0 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '—');

export function LiveMonitorPage() {
  const qc = useQueryClient();
  const [id, setId] = useState('');
  const [voiding, setVoiding] = useState<LiveAttempt | null>(null);
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const exams = useQuery({ queryKey: ['teacher-cbt-exams'], queryFn: () => getCbtExams({ per_page: 100 }).then((r) => asList<ExamRow>(unwrap(r))) });
  const live = useQuery({ queryKey: ['teacher-cbt-live', id], enabled: !!id, queryFn: () => getCbtExamsLive(id).then((r) => unwrap<Live>(r)), refetchInterval: 15_000 });
  const voidAttempt = useMutation({
    mutationFn: () => postCbtAttemptsVoid(voiding!.attempt_id, { reason: reason.trim() }),
    onSuccess: () => { setMsg({ tone: 'success', text: `Attempt by ${voiding!.name} voided.` }); setVoiding(null); setReason(''); qc.invalidateQueries({ queryKey: ['teacher-cbt-live', id] }); },
    onError: (e) => setMsg({ tone: 'error', text: errMsg(e) }),
  });
  const d = live.data;
  const events = (e: Record<string, number> | undefined) => Object.entries(e ?? {}).filter(([, n]) => n).map(([k, n]) => `${k.replace(/_/g, ' ')} ×${n}`).join(', ');

  return (
    <div className="space-y-4">
      <PageHeader title="Live test monitor" subtitle="See who is writing right now. Refreshes every 15 seconds." />
      {msg && <Banner tone={msg.tone}>{msg.text}</Banner>}
      <Card><QueryState q={exams}><div className="max-w-md"><ExamSelect exams={exams.data ?? []} value={id} onChange={setId} /></div></QueryState></Card>
      {id && (
        <QueryState q={live}>
          {d && (
            <>
              <div className="grid sm:grid-cols-4 gap-3">
                <Stat label="Eligible students" value={d.eligible} />
                <Stat label="Not started" value={d.counts.not_started} />
                <Stat label="Writing now" value={d.counts.in_progress} />
                <Stat label="Finished" value={d.counts.finished} />
              </div>
              <Card title={`Attempts · test is ${d.status}`}>
                <Table rows={d.attempts} rowKey={(r) => r.attempt_id} empty="No one has started this test yet." cols={[
                  { key: 'name', header: 'Student', render: (r) => <>{r.name}<span className="block text-xs text-slate-400">{r.admission_no}</span></> },
                  { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
                  { key: 'answered', header: 'Answered', render: (r) => `${r.answered}/${d.questions}` },
                  { key: 'left', header: 'Time left', render: (r) => <span className="font-mono">{mins(r.time_left_seconds)}</span> },
                  { key: 'saved', header: 'Last saved', render: (r) => fmtDateTime(r.last_saved_at) },
                  { key: 'flags', header: 'Signals', render: (r) => <span className={r.flagged ? 'text-red-700 font-medium' : 'text-slate-500'}>{r.flagged ? 'Flagged. ' : ''}{events(r.events) || '—'}</span> },
                  { key: 'a', header: '', render: (r) => r.status !== 'voided' ? <Button variant="danger" onClick={() => { setVoiding(r); setReason(''); }}>Void</Button> : null },
                ]} />
              </Card>
            </>
          )}
        </QueryState>
      )}
      {voiding && (
        <Modal title={`Void ${voiding.name}'s attempt`} onClose={() => setVoiding(null)}>
          <p className="text-sm text-slate-600 mb-3">The attempt will not count. The student may try again if they have attempts left.</p>
          <Input label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} placeholder="e.g. caught using a phone" />
          <div className="flex justify-end gap-2 mt-4"><Button variant="ghost" onClick={() => setVoiding(null)}>Cancel</Button><Button variant="danger" disabled={!reason.trim() || voidAttempt.isPending} onClick={() => voidAttempt.mutate()}>{voidAttempt.isPending ? 'Voiding…' : 'Void attempt'}</Button></div>
        </Modal>
      )}
    </div>
  );
}
